import React, { useMemo, useState, useEffect } from 'react';
import { useSection } from '../../../montana-demo/contexts/HeaderThemeContext';
import { MontanaPricingHero } from '@/components/communities/montana/MontanaPricingHero';
import { PricingCalculator } from '@/components/communities/montana/PricingCalculator';
// import { VideoShowcase } from '@/components/communities/montana/VideoShowcase';
import { RevolutionaryFeatures } from '@/components/communities/montana/RevolutionaryFeatures';
// import { FeatureComparison } from '@/components/communities/montana/FeatureComparison';
// import { ROICalculator } from '@/components/communities/montana/ROICalculator';
// import { IndustryInsights } from '@/components/communities/montana/IndustryInsights';
import { MontanaCTA } from '@/components/communities/montana/MontanaCTA';
import { usePricingCalculator } from '@/hooks/communities/montana';
import { useNavigate, useLocation } from 'react-router-dom';
import { pricingAnalytics } from '@/utils/entities/clients/pricingTracking';
import { AddOnsSelector } from '@/components/addons/AddOnsSelector';
import { MONTANA_ADDONS_CATALOG } from '@/data/addons/catalog.montana';
import { GLOBAL_ADDONS_CATALOG } from '@/data/addons/catalog.global';

export const MontanaPricingPage: React.FC = () => {
  // Header theme section registration
  const heroRef = useSection('pricing-hero', 'dark');
  const ctaRef = useSection('pricing-cta', 'dark');

  // Shared state for all components
  const pricingState = usePricingCalculator();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>(() => {
    const params = new URLSearchParams(window.location.search);
    const addonsParam = params.get('addons');
    return addonsParam
      ? addonsParam
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter catalog by selected tier (or show silver/gold options if none selected yet)
  const visibleAddOns = useMemo(() => {
    const tier = pricingState.selectedTier ?? 'silver';
    const source = MONTANA_ADDONS_CATALOG.length ? MONTANA_ADDONS_CATALOG : GLOBAL_ADDONS_CATALOG;
    return source.filter((a) => a.applicableTiers.includes(tier));
  }, [pricingState.selectedTier]);

  // Persist add-ons to URL when they change
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedAddOns.length > 0) {
      url.searchParams.set('addons', selectedAddOns.join(','));
    } else {
      url.searchParams.delete('addons');
    }
    window.history.replaceState({}, '', url.toString());
  }, [selectedAddOns]);

  // If navigated with #addons, scroll to the selector after render
  useEffect(() => {
    if (location.hash === '#addons') {
      setTimeout(() => {
        const el = document.getElementById('addons');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 0);
    }
  }, [location.hash]);

  // Handle tier selection from pricing calculator
  const handleSelectTier = (tier: 'silver' | 'gold' | 'custom') => {
    console.log('MontanaPricingPage handleSelectTier called with:', tier);

    // Track analytics
    pricingAnalytics.trackTierSelection(tier, 'montana_pricing_page');

    // Navigate to appropriate welcome page
    if (tier === 'custom') {
      navigate('/onboarding/welcome/contact-sales');
    } else {
      navigate(`/onboarding/welcome/${tier}`);
    }
  };

  // Event handlers for CTA actions
  const handleScheduleDemo = () => {
    // Track analytics
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (
        window as typeof window & {
          gtag: (command: string, eventName: string, parameters?: Record<string, unknown>) => void;
        }
      ).gtag;
      gtag('event', 'schedule_demo_attempt', {
        event_category: 'conversion',
        event_label: 'montana_pricing_page',
      });
    }

    // Navigate to smart demo scheduler
    navigate('/schedule-demo');
  };

  const handleContactSales = () => {
    console.log('MontanaPricingPage handleContactSales called');

    // Track analytics
    pricingAnalytics.trackContactSales('montana_pricing_page');

    // Navigate to contact sales welcome page
    navigate('/onboarding/welcome/contact-sales');
  };

  const handleGetStarted = () => {
    // Track analytics
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (
        window as typeof window & {
          gtag: (command: string, eventName: string, parameters?: Record<string, unknown>) => void;
        }
      ).gtag;
      gtag('event', 'get_started', {
        event_category: 'conversion',
        event_label: 'montana_pricing_page',
      });
    }

    // Route to appropriate assessment based on selected tier
    const tier = pricingState.selectedTier || 'silver';
    const addons = selectedAddOns.length > 0 ? `&addons=${selectedAddOns.join(',')}` : '';

    if (tier === 'custom') {
      // Custom tier goes to contact sales
      navigate('/onboarding/welcome/contact-sales');
    } else {
      // Silver/Gold go to pricing assessment
      navigate(`/pricing/assessment?tier=${tier}${addons}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO and Meta Tags would be handled by parent router/layout */}

      {/* Hero Section with Integrated Calculator */}
      <section ref={heroRef}>
        <MontanaPricingHero
          onGetStarted={handleGetStarted}
          onContactSales={handleContactSales}
          onScheduleDemo={handleScheduleDemo}
          pricingState={pricingState}
          selectedAddOnNames={selectedAddOns
            .map((id) => MONTANA_ADDONS_CATALOG.find((a) => a.id === id)?.name)
            .filter((x): x is string => Boolean(x))}
        />
      </section>

      {/* Revolutionary Features Section */}
      <RevolutionaryFeatures
        onScheduleDemo={handleScheduleDemo}
        onSeeLiveDemo={handleScheduleDemo}
      />

      {/* Detailed Pricing Calculator */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Calculate Your Community's Savings
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See exactly how much your Montana community can save while improving efficiency and
              resident satisfaction.
            </p>
          </div>
          <PricingCalculator
            onSelectTier={handleSelectTier}
            onContactSales={handleContactSales}
            onScheduleDemo={handleScheduleDemo}
            pricingState={pricingState}
            extraNavigateSearch={
              selectedAddOns.length ? `?addons=${selectedAddOns.join(',')}` : undefined
            }
          />

          {/* Add-ons Selector (Montana curated) */}
          <div id="addons" className="mt-10 addons-selector">
            <AddOnsSelector
              items={visibleAddOns}
              selected={selectedAddOns}
              onChange={setSelectedAddOns}
              title="Optional Add-ons for Montana"
              subtitle="Quote-based items are scoped separately based on your community's organization level."
            />
            {selectedAddOns.length > 0 && (
              <p className="text-xs text-gray-600 mt-2">
                Selected add-ons will be noted in your proposal and discussed during scoping.
              </p>
            )}
          </div>
        </div>
      </section>
      {/* Temporarily commented out until we have verified competitor data */}
      {/*
      <section className="py-16 bg-linear-to-br from-green-50 via-blue-50 to-indigo-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See How We Compare
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare our Montana-optimized platform against traditional community 
              management solutions and see why we're the clear choice.
            </p>
          </div>
          <FeatureComparison />
        </div>
      </section>
      */}

      {/* Video Showcase Section */}
      {/* VideoShowcase component moved to demo page */}

      {/* ROI Calculator */}
      {/* Temporarily commented out since it relies on competitor comparisons we can't verify */}
      {/*
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Calculate Your Return on Investment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the measurable impact on your community's operations, 
              finances, and resident satisfaction.
            </p>
          </div>
          <ROICalculator />
        </div>
      </section>
      */}

      {/* Success Stories & Testimonials */}
      {/* IndustryInsights component moved to governance page */}

      {/* Final Call-to-Action */}
      <section ref={ctaRef}>
        <MontanaCTA
          onScheduleDemo={handleScheduleDemo}
          onContactSales={handleContactSales}
          onGetStarted={handleGetStarted}
        />
      </section>
    </div>
  );
};
